<template>
  <div class="profit-chart">
    <div class="profit-chart__filters">
      <div>
        <slot name="filter-options" />
      </div>

      <div class="profit-chart__select-filter">
        <SpinnerLoader
          v-if="isLoading"
          class="profit-chart__filter-loader"
          :size="16"
          :color="NebraskaColors.elementPlaceholder"
        />
        <FilterSelect
          v-if="periodFilters"
          v-model="periodValue"
          label="Período"
          use-option-label
          :options="formattedFilters"
          render-menu-with-portal
          :disabled="isLoading"
          data-testid="date-select"
          @change="fetchProventsChart"
        />
      </div>
    </div>

    <MainLoading v-if="isLoading" />
    <MainError
      v-else-if="hasError"
      title="Não foi possivel carregar os proventos desse ano"
      description="Aguarde alguns minutos e tente novamente."
      @on-retry="fetchProventsChart"
    />

    <template v-else>
      <div class="profit-chart__value">
        <HeadingDisplay size="extra-small" head="Proventos recebidos" title="">
          <template #title-component>
            <Money>{{ profitValue }}</Money>
          </template>
        </HeadingDisplay>
      </div>

      <BarChart
        v-if="series"
        :max-axis-y="maxAxisY"
        :labels="labels"
        :series="series"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  FilterSelect,
  HeadingDisplay,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';
import { BarChart } from '@/modules/common/components/charts/v2/BarChart';
import { SeriesOptionsType } from 'highcharts';
import { MainError } from '@/modules/trade/components/MainError';
import { MainLoading } from '@/modules/trade/components/MainLoading';
import { Money } from '@/modules/common/components/Money';

import {
  getProfitChart,
  PerformanceTypeFilter,
  IProfitChartData
} from '@/modules/wealth/services/portfolio';

interface Props {
  portfolioId: string;
}
const props = defineProps<Props>();

const periodFilters = ref<PerformanceTypeFilter[]>([
  { label: 'Ano', key: 'annual', active: true }
]);
const periodValue = ref('annual');

const series = ref<SeriesOptionsType[] | null>(null);
const labels = ref<string[]>([]);
const profitValue = ref('');
const maxAxisY = ref<number | undefined>(undefined);

const isLoading = ref(true);
const hasError = ref(false);

const formattedFilters = computed(() => {
  return periodFilters.value?.map(filter => {
    return {
      value: filter.key,
      label: filter.label
    };
  });
});

function getSeries(data: IProfitChartData[]) {
  const formattedValues: number[] = [];

  data.forEach(item => {
    formattedValues.push(item.earnings.value);
  });

  maxAxisY.value = Math.max(...formattedValues);

  return [
    {
      type: 'column',
      name: 'Proventos',
      data: formattedValues,
      color: '#333131'
    }
  ];
}

async function fetchProventsChart() {
  try {
    isLoading.value = true;
    hasError.value = false;

    const axiosX: string[] = [];

    const profit = await getProfitChart(props.portfolioId, periodValue.value);

    profitValue.value = profit.totalEarnings;
    periodFilters.value = profit.periodFilters;

    profit.data.forEach(item => {
      axiosX.push(item.label);
      labels.value = axiosX;
    });

    series.value = getSeries(profit.data) as SeriesOptionsType[];
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onBeforeMount(fetchProventsChart);
</script>

<style lang="less" scoped>
.profit-chart {
  &__filters {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: @size-spacing-x600;
  }

  &__select-filter {
    display: flex;
    align-items: center;
  }

  &__filter-loader {
    margin-right: @size-spacing-x300;
  }

  &__value {
    margin-bottom: @size-spacing-x500;
  }
}
</style>
